import { Plus, Snowflake, Trash2 } from '@orgus/ui/icons';
import { useEffect, useMemo, useState } from 'react';
import type { MaintenanceWindow, UpdateChannel, UpdateMode, UpdatePolicy, UpdatePolicyInput } from '@orgus/protocol';
import { CHANNEL_LABEL, updatePolicyProblem } from '@orgus/protocol';
import { Button, Checkbox, Dialog, ErrorState, IconButton, Input, Segmented, Select, formatDate, formatDuration, formatPercent, localTimeZone, toUiError } from '@orgus/ui';
import { CANARY_SHARES, DAY_NAMES, EARLY_SHARES, HALT_RATES, SOAKS_MS } from './model.ts';
import { fleetActions } from './store.ts';

const DAY = 86_400_000;

interface Draft {
  mode: UpdateMode;
  channel: UpdateChannel;
  canary: number;
  canarySoak: number;
  early: number;
  earlySoak: number;
  haltRate: number;
  windowOn: boolean;
  window: MaintenanceWindow;
  timeZone: string;
  freezes: Array<{ id?: string; from: number; until: number; reason: string }>;
}

const soakLabel = (ms: number) => (ms === 0 ? 'No soak' : `Soak ${formatDuration(ms)}`);
const nearest = (list: number[], v: number) => list.reduce((a, b) => (Math.abs(b - v) < Math.abs(a - v) ? b : a), list[0]!);

function draftOf(p: UpdatePolicy): Draft {
  const [a, b] = p.stages;
  return {
    mode: p.mode,
    channel: p.channel,
    canary: nearest(CANARY_SHARES, a?.percent ?? 5),
    canarySoak: nearest(SOAKS_MS, a?.soakMs ?? 3_600_000),
    early: nearest(EARLY_SHARES, p.stages.length > 2 ? (b?.percent ?? 25) : 25),
    earlySoak: nearest(SOAKS_MS, p.stages.length > 2 ? (b?.soakMs ?? 4 * 3_600_000) : 4 * 3_600_000),
    haltRate: nearest(HALT_RATES, p.haltFailureRate),
    windowOn: p.windows.length > 0,
    window: p.windows[0] ? { ...p.windows[0], days: [...p.windows[0].days] } : { days: [1, 2, 3, 4, 5], start: '22:00', end: '06:00' },
    timeZone: p.timeZone,
    freezes: p.freezes.map((f) => ({ id: f.id, from: f.from, until: f.until, reason: f.reason })),
  };
}

function inputOf(d: Draft): UpdatePolicyInput {
  return {
    mode: d.mode,
    channel: d.channel,
    stages: [
      { percent: d.canary, soakMs: d.canarySoak },
      { percent: Math.max(d.early, d.canary + 1), soakMs: d.earlySoak },
      { percent: 100, soakMs: 0 },
    ],
    haltFailureRate: d.haltRate,
    windows: d.windowOn ? [d.window] : [],
    timeZone: d.timeZone,
    freezes: d.freezes,
  };
}

/** A date input's value as the start of that day here, and back. */
const dateValue = (ts: number) => {
  const d = new Date(ts);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const fromDateValue = (v: string): number | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).getTime() : null;
};

/**
 * The update policy: automatic or manual, the channel, the stages and soaks, when a rollout
 * halts, the maintenance window and freezes. One form; what failed is said inside it.
 */
export function UpdateSettingsDialog({ open, onOpenChange, policy }: { open: boolean; onOpenChange: (o: boolean) => void; policy: UpdatePolicy }) {
  const [d, setD] = useState<Draft>(() => draftOf(policy));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [freezeReason, setFreezeReason] = useState('');
  const [freezeUntil, setFreezeUntil] = useState(() => dateValue(Date.now() + 7 * DAY));

  useEffect(() => {
    if (!open) return;
    setD(draftOf(policy));
    setError(null);
    setFreezeReason('');
  }, [open]);

  const set = (patch: Partial<Draft>) => setD((x) => ({ ...x, ...patch }));
  const input = useMemo(() => inputOf(d), [d]);
  const problem = updatePolicyProblem(input);
  const zones = useMemo(() => [...new Set(['UTC', localTimeZone(), d.timeZone])].map((z) => ({ value: z, label: z })), [d.timeZone]);
  const auto = d.mode === 'auto';

  const addFreeze = () => {
    const until = fromDateValue(freezeUntil);
    if (until === null || !freezeReason.trim()) return;
    set({ freezes: [...d.freezes, { from: Date.now(), until: until + DAY, reason: freezeReason.trim() }] });
    setFreezeReason('');
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await fleetActions.savePolicy(input);
      onOpenChange(false);
    } catch (e) {
      setError(toUiError(e, { what: 'The update settings were not saved.' }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title="Agent updates"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="primary" loading={saving} disabled={problem != null} onClick={() => void save()}>
            Save settings
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-6">
        {error != null && <ErrorState variant="inline" error={error} />}

        <section aria-labelledby="upd-how" className="grid gap-4 sm:grid-cols-2">
          <h3 id="upd-how" className="sr-only">
            How updates arrive
          </h3>
          <div className="flex flex-col gap-2">
            <span className="text-ui font-medium text-fg">Updates</span>
            <Segmented<UpdateMode>
              label="Updates"
              value={d.mode}
              onChange={(mode) => set({ mode })}
              options={[
                { value: 'auto', label: 'Automatic' },
                { value: 'manual', label: 'Manual' },
              ]}
            />
            <p className="text-caption text-fg-subtle">{auto ? 'Each new release rolls out by itself.' : 'An admin starts each rollout from Machines.'}</p>
          </div>
          <div className="flex flex-col gap-2">
            <span className="text-ui font-medium text-fg">Channel</span>
            <Segmented<UpdateChannel>
              label="Channel"
              value={d.channel}
              onChange={(channel) => set({ channel })}
              options={(['stable', 'beta'] as const).map((c) => ({ value: c, label: CHANNEL_LABEL[c] }))}
            />
            <p className="text-caption text-fg-subtle">{d.channel === 'stable' ? 'Releases that passed beta.' : 'Releases a week or two early. A machine can follow its own channel.'}</p>
          </div>
        </section>

        <section aria-labelledby="upd-stages" className="flex flex-col gap-3 border-t border-border-subtle pt-4">
          <h3 id="upd-stages" className="text-ui font-medium text-fg">
            Stages
          </h3>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="flex flex-col gap-2 rounded-card border border-border-subtle p-3">
              <span className="text-caption text-fg-muted">1. Canary</span>
              <Select size="sm" aria-label="Canary share" value={String(d.canary)} onValueChange={(v) => set({ canary: Number(v) })} options={CANARY_SHARES.map((p) => ({ value: String(p), label: `${formatPercent(p / 100)} and the test ring` }))} />
              <Select size="sm" aria-label="Canary soak" value={String(d.canarySoak)} onValueChange={(v) => set({ canarySoak: Number(v) })} options={SOAKS_MS.map((ms) => ({ value: String(ms), label: soakLabel(ms) }))} />
            </div>
            <div className="flex flex-col gap-2 rounded-card border border-border-subtle p-3">
              <span className="text-caption text-fg-muted">2. Early</span>
              <Select size="sm" aria-label="Early share" value={String(d.early)} onValueChange={(v) => set({ early: Number(v) })} options={EARLY_SHARES.map((p) => ({ value: String(p), label: formatPercent(p / 100) }))} />
              <Select size="sm" aria-label="Early soak" value={String(d.earlySoak)} onValueChange={(v) => set({ earlySoak: Number(v) })} options={SOAKS_MS.map((ms) => ({ value: String(ms), label: soakLabel(ms) }))} />
            </div>
            <div className="flex flex-col gap-2 rounded-card border border-border-subtle p-3">
              <span className="text-caption text-fg-muted">3. Everyone</span>
              <span className="text-ui text-fg">All machines</span>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-ui text-fg-muted">Halt when failures reach</span>
            <Select size="sm" aria-label="Halt rate" className="w-32" value={String(d.haltRate)} onValueChange={(v) => set({ haltRate: Number(v) })} options={HALT_RATES.map((r) => ({ value: String(r), label: formatPercent(r) }))} />
            <span className="text-caption text-fg-subtle">of the machines offered so far</span>
          </div>
        </section>

        <section aria-labelledby="upd-window" className="flex flex-col gap-3 border-t border-border-subtle pt-4">
          <h3 id="upd-window" className="text-ui font-medium text-fg">
            When
          </h3>
          <Checkbox checked={d.windowOn} onCheckedChange={(windowOn) => set({ windowOn })} label="Only inside a maintenance window" hint="Updates started by hand still run outside it." />
          {d.windowOn && (
            <div className="flex flex-col gap-3 pl-6">
              <div className="flex flex-wrap gap-3" role="group" aria-label="Days">
                {DAY_NAMES.map((name, i) => (
                  <Checkbox
                    key={name}
                    label={name}
                    checked={d.window.days.includes(i)}
                    onCheckedChange={(on) => set({ window: { ...d.window, days: on ? [...d.window.days, i].sort() : d.window.days.filter((x) => x !== i) } })}
                  />
                ))}
              </div>
              <div className="grid gap-3 sm:grid-cols-3">
                <Input size="sm" type="time" label="Opens" value={d.window.start} onChange={(e) => set({ window: { ...d.window, start: e.target.value } })} />
                <Input size="sm" type="time" label="Closes" value={d.window.end} onChange={(e) => set({ window: { ...d.window, end: e.target.value } })} />
                <Select size="sm" label="Time zone" value={d.timeZone} onValueChange={(timeZone) => set({ timeZone })} options={zones} />
              </div>
            </div>
          )}
        </section>

        <section aria-labelledby="upd-freeze" className="flex flex-col gap-3 border-t border-border-subtle pt-4">
          <h3 id="upd-freeze" className="text-ui font-medium text-fg">
            Freezes
          </h3>
          <p className="text-caption text-fg-subtle">No machine is offered an update during a freeze, automatic or by hand.</p>
          {d.freezes.length > 0 && (
            <ul className="flex flex-col divide-y divide-border-subtle rounded-card border border-border-subtle">
              {d.freezes.map((f, i) => (
                <li key={f.id ?? `new-${i}`} className="flex h-row items-center gap-3 px-3">
                  <Snowflake className="size-4 shrink-0 text-fg-subtle" aria-hidden />
                  <span className="min-w-0 flex-1 truncate text-ui text-fg">{f.reason}</span>
                  <span className="text-caption text-fg-subtle num">
                    {formatDate(f.from)} to {formatDate(f.until)}
                  </span>
                  <IconButton size="xs" variant="ghost" label={`Remove the freeze ${f.reason}`} icon={<Trash2 />} onClick={() => set({ freezes: d.freezes.filter((_, k) => k !== i) })} />
                </li>
              ))}
            </ul>
          )}
          <div className="flex flex-wrap items-end gap-3">
            <Input size="sm" label="Reason" placeholder="Release week" value={freezeReason} onChange={(e) => setFreezeReason(e.target.value)} wrapperClassName="min-w-48 flex-1" />
            <Input size="sm" type="date" label="Until" value={freezeUntil} onChange={(e) => setFreezeUntil(e.target.value)} />
            <Button size="sm" variant="secondary" icon={<Plus />} disabled={!freezeReason.trim() || fromDateValue(freezeUntil) === null} onClick={addFreeze}>
              Add freeze
            </Button>
          </div>
        </section>

        {problem && <p className="text-caption text-status-danger">{problem}</p>}
      </div>
    </Dialog>
  );
}
